import {useState} from 'react'
import {useSearchParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Tag} from '@/components/ui/tag'
import {ConfirmDialog} from '@/components/confirm-dialog'
import {DesignationFormDialog, type DesignationFormInitial} from '@/components/designation-form-dialog'
import {InactiveBadge} from '@/components/inactive-badge'
import {LicenciaBadge} from '@/components/licencia-badge'
import {PlanGate} from '@/components/plan-upsell'
import {useBusiness, useCapabilities} from '@/hooks/empresa/organizacion/use-businesses'
import type {Employee} from '@/hooks/empresa/identidad/use-employees'
import {
  COMMITTEE_STANDARDS,
  RESPONSABLE_STANDARD,
  isExpired,
  useConfirmDesignation,
  useDesignations,
  useRemoveDesignation,
  type Designation,
} from '@/hooks/sgsst/evaluacion/use-designations'
import {useSgsst} from '@/hooks/sgsst/evaluacion/use-sgsst'
import {formatDateOnly} from '@/lib/risk'
import {documentoCorto} from '@/lib/documento'

function personName(designation: Designation): string {
  const person = designation.employee
  return person ? `${person.name} ${person.last_name}` : ''
}

function parityShortfalls(
  cards: Designation[]
): {count: number; rol: 'principal' | 'suplente'; party: 'empleador' | 'trabajadores'}[] {
  // Vencidas don't hold a seat: parity counts only live, unexpired cards.
  const seated = cards.filter((c) => !c.archived_at && !isExpired(c))
  const shortfalls: {count: number; rol: 'principal' | 'suplente'; party: 'empleador' | 'trabajadores'}[] = []
  for (const rol of ['principal', 'suplente'] as const) {
    const employer = seated.filter((c) => c.rol === rol && c.party === 'empleador').length
    const workers = seated.filter((c) => c.rol === rol && c.party === 'trabajadores').length
    if (employer > workers) shortfalls.push({count: employer - workers, rol, party: 'trabajadores'})
    if (workers > employer) shortfalls.push({count: workers - employer, rol, party: 'empleador'})
  }
  return shortfalls
}

function ParityNotice({cards}: {cards: Designation[]}) {
  const {t} = useTranslation()
  const shortfalls = parityShortfalls(cards)
  if (shortfalls.length === 0) return null
  const sides = {
    empleador: t('designations.parity.sideEmpleador'),
    trabajadores: t('designations.parity.sideTrabajadores'),
  }
  const roles = {
    principal: [t('designations.parity.rolePrincipal'), t('designations.parity.rolePrincipalPlural')],
    suplente: [t('designations.parity.roleSuplente'), t('designations.parity.roleSuplentePlural')],
  }
  return (
    <p className="tone-amber rounded-lg px-3 py-2 text-xs" data-testid="designation-parity">
      {shortfalls
        .map((s) =>
          t('designations.parity.missing', {
            count: s.count,
            role: roles[s.rol][0],
            rolePlural: roles[s.rol][1],
            side: sides[s.party],
          })
        )
        .join(' · ')}
    </p>
  )
}

function CardStamp({tone, children}: {tone: 'amber' | 'red' | 'neutral'; children: React.ReactNode}) {
  const toneClass = {amber: 'tone-amber', red: 'tone-red', neutral: 'bg-secondary text-muted-foreground'}[tone]
  return (
    <p className={`${toneClass} rounded px-1.5 py-0.5 font-mono text-[9px] tracking-wider uppercase`}>{children}</p>
  )
}

function DesignationCard({
  designation,
  editable,
  onConfirm,
  onReplace,
  onRemove,
}: {
  designation: Designation
  editable: boolean
  onConfirm: () => void
  onReplace: () => void
  onRemove: () => void
}) {
  const {t} = useTranslation()
  const archived = !!designation.archived_at
  const expired = isExpired(designation)
  const person = designation.employee
  return (
    <div
      data-testid="designation-card"
      className={`overflow-hidden rounded-[11px] border border-border bg-card ${archived ? 'opacity-60' : ''}`}>
      <div className="flex h-9 items-center justify-center bg-accent font-mono text-[10px] font-semibold tracking-wider text-accent-foreground uppercase">
        {t('designations.cardHeader')}
      </div>
      <div className="space-y-1.5 p-2.5">
        <p className="text-xs font-semibold break-words text-foreground">{personName(designation)}</p>
        {person && (
          <p className="font-mono text-[10px] text-muted-foreground">
            {documentoCorto(person.tipo_documento, person.cedula)}
          </p>
        )}
        {designation.employee && (
          <LicenciaBadge
            estado={designation.employee.licencia_estado}
            categoria={designation.employee.licencia_categoria}
            testId={`designation-${designation.id}-licencia`}
          />
        )}
        {designation.party && designation.rol && (
          <div className="flex flex-wrap gap-1">
            <Tag tone="neutral">{t(`designations.party.${designation.party}`)}</Tag>
            <Tag tone="neutral">{t(`designations.rol.${designation.rol}`)}</Tag>
          </div>
        )}
        <p className="font-mono text-[10px] text-muted-foreground">
          {t('designations.since', {date: formatDateOnly(designation.effective_date)})}
        </p>
        {designation.term_end &&
          (expired ? (
            <CardStamp tone="red">
              {t('designations.expired')} · {formatDateOnly(designation.term_end)}
            </CardStamp>
          ) : (
            <CardStamp tone="neutral">
              {t('designations.termEnds', {date: formatDateOnly(designation.term_end)})}
            </CardStamp>
          ))}
        {designation.carta ? (
          <a
            href={designation.carta.download_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex max-w-full items-center gap-1.5 rounded-md bg-secondary px-2 py-0.5 text-[11px] font-medium hover:text-primary">
            <span className="truncate">{designation.carta.filename}</span>
            <span aria-hidden="true" className="font-semibold text-primary">
              ↓
            </span>
          </a>
        ) : (
          designation.standard_code === RESPONSABLE_STANDARD &&
          !archived && <CardStamp tone="amber">{t('designations.missingCarta')}</CardStamp>
        )}
        {designation.employee && !designation.employee.active && !archived && (
          <InactiveBadge since={designation.employee.inactive_since} />
        )}
        {archived ? (
          <CardStamp tone="neutral">
            {t('designations.archivedSince', {date: formatDateOnly(designation.archived_at!.slice(0, 10))})}
          </CardStamp>
        ) : (
          <>
            {designation.pending_confirmation && <CardStamp tone="amber">{t('designations.pendingTag')}</CardStamp>}
            {editable && (
              <div className="flex flex-wrap gap-1 pt-0.5">
                {designation.pending_confirmation && (
                  <Button size="sm" className="h-6 px-2 text-[11px]" onClick={onConfirm}>
                    {t('designations.confirm')}
                  </Button>
                )}
                <Button size="sm" variant="outline" className="h-6 px-2 text-[11px]" onClick={onReplace}>
                  {t('designations.replace')}
                </Button>
                <Button size="sm" variant="ghost" className="h-6 px-2 text-[11px]" onClick={onRemove}>
                  {t('designations.remove')}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

/** Renders nothing on ordinary standards. */
export function DesignationsBlock({
  businessId,
  standardCode,
  roster,
  editable,
}: {
  businessId: string
  standardCode: string
  roster: Employee[]
  editable: boolean
}) {
  const {t} = useTranslation()
  const [searchParams] = useSearchParams()
  const viewedYear = searchParams.get('year') ? Number(searchParams.get('year')) : undefined
  const {data: state} = useSgsst(businessId, viewedYear)
  const item = state?.assessment?.items.find((i) => i.code === standardCode)
  const scoped = !!item?.designation_scoped
  const {data} = useDesignations(scoped ? businessId : undefined, viewedYear)
  const remove = useRemoveDesignation(businessId)
  const confirm = useConfirmDesignation(businessId)
  const {data: business} = useBusiness(businessId)
  const {can_use_designaciones} = useCapabilities(businessId)
  const [formOpen, setFormOpen] = useState(false)
  const [formInitial, setFormInitial] = useState<DesignationFormInitial>({})
  const [replaceTarget, setReplaceTarget] = useState<Designation | null>(null)
  const [removeTarget, setRemoveTarget] = useState<Designation | null>(null)

  if (!scoped) return null

  const cards = (data?.items ?? []).filter((d) => d.standard_code === standardCode)
  const live = cards.filter((d) => !d.archived_at)
  const vigentes = live.filter((d) => !isExpired(d))
  const pendingCount = live.filter((d) => d.pending_confirmation).length
  const isCommittee = COMMITTEE_STANDARDS.has(standardCode)
  // Free tier: designación mutations are paid; the cards stay visible as compliance evidence.
  const canMutate = editable && can_use_designaciones

  function replaceConfirmed() {
    if (!replaceTarget) return
    const target = replaceTarget
    remove.mutate(target.id, {
      onSuccess: () => {
        setReplaceTarget(null)
        setFormInitial({party: target.party, rol: target.rol})
        setFormOpen(true)
      },
    })
  }

  return (
    <div className="space-y-3" data-testid={`designations-${standardCode}`}>
      <div className="flex flex-wrap items-center gap-2">
        <p className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
          {t('designations.summary', {count: vigentes.length})}
        </p>
        {pendingCount > 0 && (
          <span className="tone-amber rounded-full px-2 py-0.5 font-mono text-[10px]">
            {t('designations.pendingCount', {count: pendingCount})}
          </span>
        )}
        {editable && (
          <PlanGate
            locked={!can_use_designaciones}
            feature={t('designations.register')}
            businessId={businessId}
            businessName={business?.name}>
            <Button
              size="sm"
              data-testid={`designation-register-${standardCode}`}
              onClick={() => {
                setFormInitial({})
                setFormOpen(true)
              }}>
              {t('designations.register')}
            </Button>
          </PlanGate>
        )}
      </div>

      {isCommittee && <ParityNotice cards={cards} />}

      {cards.length > 0 && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {cards.map((designation) => (
            <DesignationCard
              key={designation.id}
              designation={designation}
              editable={canMutate}
              onConfirm={() => confirm.mutate(designation.id)}
              onReplace={() => setReplaceTarget(designation)}
              onRemove={() => setRemoveTarget(designation)}
            />
          ))}
        </div>
      )}

      <DesignationFormDialog
        businessId={businessId}
        standardCode={standardCode}
        standardName={item?.name ?? ''}
        roster={roster}
        open={formOpen}
        onOpenChange={setFormOpen}
        initial={formInitial}
      />
      <ConfirmDialog
        open={!!replaceTarget}
        onOpenChange={(open) => !open && setReplaceTarget(null)}
        title={t('designations.replaceTitle')}
        description={t('designations.replaceBody', {name: replaceTarget ? personName(replaceTarget) : ''})}
        confirmLabel={t('designations.replaceAction')}
        cancelLabel={t('common.cancel')}
        pending={remove.isPending}
        onConfirm={replaceConfirmed}
      />
      <ConfirmDialog
        open={!!removeTarget}
        onOpenChange={(open) => !open && setRemoveTarget(null)}
        title={t('designations.removeTitle')}
        description={t('designations.removeBody', {name: removeTarget ? personName(removeTarget) : ''})}
        confirmLabel={t('designations.remove')}
        cancelLabel={t('common.cancel')}
        pending={remove.isPending}
        onConfirm={() => removeTarget && remove.mutate(removeTarget.id, {onSuccess: () => setRemoveTarget(null)})}
      />
    </div>
  )
}
